import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import { ContactDetailsOverviewPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import OverviewInformation from './Overview'

const ContactsDetailsOverviewPage = ({
  contactType,
}: {
  contactType: FinanceValueObjectsContactTypeEnum
}) => {
  const { company } = useCurrentSession()
  const { contactId } = useParams()

  const { data, loading, error } = useQuery(ContactDetailsOverviewPageDocument, {
    variables: {
      id: String(contactId),
      companyId: company.id,
    },
  })
  const contact = data?.finance?.contact

  const canSeeContacts = data?.finance._policies.contactsSee.can ?? false

  const hasClientFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const hasProjectsManagementFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_management
  )?.available

  const seeProjectsUpsell = !hasClientFeature && !hasProjectsManagementFeature
  const seeProjectsUpgrade = !hasClientFeature && hasProjectsManagementFeature

  const canManageProjects = data?.projectManagement._policies.projectManage.can ?? false
  const canSeeProjects = canManageProjects && hasClientFeature

  const canSeePurchaseOrders = data?.procurement._policies.purchaseOrderRead.can ?? false

  const canManagePurchaseFinancialDocuments =
    data?.finance._policies.financialDocumentsManagePurchase.can ?? false

  useEffect(() => {
    track(trackingEvents.accounting.viewContactDetailsPage, {
      view: 'overview',
      projects_upsell: seeProjectsUpsell || seeProjectsUpgrade,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ResponsivePage.Body
      authorized={canSeeContacts}
      error={error}
      loading={loading}
      variant="fullscreen"
    >
      {contact && (
        <OverviewInformation
          contact={contact}
          canSeeProjects={canSeeProjects}
          canSeePurchaseOrders={canSeePurchaseOrders}
          canManageFinancialDocuments={canManagePurchaseFinancialDocuments}
          contactType={contactType}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContactsDetailsOverviewPage
